import { extends as _extends, slicedToArray as _slicedToArray } from '../../_virtual/_rollupPluginBabelHelpers.js';
import React from 'react';
import isEmail from 'sane-email-validation';
import TextField from './TextField.js';
import useConfigWithExtraValidation from '../../utils/form/useExtraFieldValidation.js';

/**
 * Validates an email address and returns an error message if invalid.
 * The validation checks for:
 * - Basic email format using sane-email-validation
 * - Trims leading/trailing spaces
 * - Rejects internal spaces
 *
 * @param {string} value - The email address to validate.
 * @returns {string|null} Returns 'Invalid email' if email is invalid, null otherwise
 */
function getInvalidEmailError(value) {
  if (!value) {
    return null; // Let required field validation handle empty values
  }
  var trimmedValue = value.trim();
  if (trimmedValue.length === 0) {
    return null;
  }

  // Check for internal spaces
  if (trimmedValue.includes(' ')) {
    return 'Email address cannot contain spaces';
  }

  // Check for multiple @ symbols
  var atCount = (trimmedValue.match(/@/g) || []).length;
  if (atCount > 1) {
    return 'Invalid email format';
  }

  // Basic format check
  if (!trimmedValue.includes('@')) {
    return 'Please include an @ in the email address';
  }
  var _trimmedValue$split = trimmedValue.split('@'),
    _trimmedValue$split2 = _slicedToArray(_trimmedValue$split, 2),
    localPart = _trimmedValue$split2[0],
    domain = _trimmedValue$split2[1];

  // Check local part
  if (!localPart) {
    return 'Please enter a part before @';
  }

  // Check domain part
  if (!domain) {
    return 'Please enter a part following @';
  }
  if (!domain.includes('.')) {
    return 'Please include a domain extension (e.g. .com)';
  }

  // Use sane-email-validation for final validation
  if (!isEmail(trimmedValue)) {
    return 'Invalid email format';
  }
  return null;
}

/**
 * A specialized text field component for email input with built-in validation.
 * This component extends TextField with email-specific functionality including:
 *
 * @component
 * @param {Object} textFieldProps - Props inherited from TextField component.
 * @param {string} textFieldProps.name - The name of the field.
 * @param {string} textFieldProps.label - The label to display for the field.
 * @param {boolean} [textFieldProps.required] - Whether the field is required.
 * @param {Object} [textFieldProps.config] - Additional configuration for the field.
 */
function EmailField(textFieldProps) {
  return /*#__PURE__*/React.createElement(TextField, _extends({}, textFieldProps, {
    type: "email",
    autoComplete: "email"
    // Add email validation
    ,
    config: useConfigWithExtraValidation(textFieldProps.config, getInvalidEmailError)
  }));
}
EmailField.propTypes = process.env.NODE_ENV !== "production" ? TextField.propTypes : {};
EmailField.defaultProps = TextField.defaultProps;

export { EmailField as default, getInvalidEmailError };
